import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useHits, useInstantSearch, useSortBy } from 'react-instantsearch';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { useGQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { useI18nSortByItems } from '@sorare/core/src/hooks/search/useI18nSortByItems';
import { useMarketplaceLifecycle } from '@sorare/core/src/hooks/useMarketplaceLifecycle';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { isType } from '@sorare/core/src/lib/gql';
import type { StatsRarity } from '@sorare/core/src/lib/playerSearchView';

import { anyPlayerUpcomingGamesAnyGameStatFragment } from '#src/components/player/AnyPlayerUpcomingGames/index.tsx';
import type { MarketValues } from '#src/components/searchPlayerTable/PriceCell/index.tsx';
import { SearchPlayerRow } from '#src/components/searchPlayerTable/SearchPlayerRow/index.tsx';
import { SearchPlayerRowSkeletons } from '#src/components/searchPlayerTable/SearchPlayerRowSkeletons/index.tsx';
import { SearchPlayerTableHeader } from '#src/components/searchPlayerTable/SearchPlayerTableHeader/index.tsx';
import { SearchTable } from '#src/components/searchPlayerTable/SearchTable/index.tsx';
import type { PlayerResultsProps } from '#src/searchCommonPlayer/AdvancedPlayerSearch/types.ts';
import {
  teamModesOfGameStats,
  useTeamMode,
} from '#src/searchCommonPlayer/teamMode.ts';

import type { BackendSearchPlayerTable_AnyPlayerInterface } from './__generated__/index.graphql.ts';

type Hit = {
  anyPlayer: BackendSearchPlayerTable_AnyPlayerInterface;
} & MarketValues;

export const SearchPlayerTable = ({
  indexes = [],
  onStatsClick,
  onListingsClick,
  watchlistId,
  favoritesWatchlist,
  defaultRowsNumber = 20,
}: PlayerResultsProps & {
  onStatsClick: (playerSlug: string) => void;
  onListingsClick: (playerSlug: string, rarity: StatsRarity) => void;
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  defaultRowsNumber?: number;
}) => {
  const sortItems = useI18nSortByItems(indexes);
  const { updateSort } = useMarketplaceLifecycle();

  const { refine, currentRefinement } = useSortBy({
    items: sortItems,
  });
  const { status } = useInstantSearch();
  const { items: commonPlayers, results } = useHits<Hit>();
  const loading = status === 'loading' || status === 'stalled';

  const sorts = (currentRefinement?.split(',') ||
    []) as PrintablePlayerSortName[];

  const sortBy = (_sorts: PrintablePlayerSortName[]) => {
    updateSort(_sorts);
    refine(_sorts.join(','));
  };

  const rowsNumber =
    (watchlistId && results?.nbHits) ||
    ((results?.nbHits || 0) < defaultRowsNumber && results?.nbHits) ||
    20;
  return (
    <SearchTable
      header={<SearchPlayerTableHeader sorts={sorts} refine={sortBy} />}
    >
      {loading ? (
        <SearchPlayerRowSkeletons rowsNumber={rowsNumber} />
      ) : (
        commonPlayers.map(({ anyPlayer, ...marketValues }) => {
          return (
            <SearchPlayerRow
              key={anyPlayer.slug}
              anyPlayer={anyPlayer}
              marketValues={marketValues}
              onStatsClick={() => onStatsClick(anyPlayer.slug)}
              onListingsClick={rarity =>
                onListingsClick(anyPlayer.slug, rarity)
              }
              watchlistId={watchlistId}
              favoritesWatchlist={favoritesWatchlist}
            />
          );
        })
      )}
    </SearchTable>
  );
};

export const BackendSearchPlayerTable = ({
  items,
  loading,
  onStatsClick,
  onListingsClick,
  watchlistId,
  favoritesWatchlist,
  defaultRowsNumber = 20,
}: PlayerResultsProps & {
  items?: ({
    anyPlayer: BackendSearchPlayerTable_AnyPlayerInterface;
  } & MarketValues)[];
  loading: boolean;
  onStatsClick: (playerSlug: string) => void;
  onListingsClick: (playerSlug: string, rarity: StatsRarity) => void;
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  defaultRowsNumber?: number;
}) => {
  const { setSearchParams, sorts } = useGQLSearchContext()!;
  const { updateSort } = useMarketplaceLifecycle();
  const teamMode = useTeamMode();
  const isScopedTeamMode = teamMode !== 'ALL';

  const results = { nbHits: 50 };
  const commonPlayers = items ?? [];

  const sortBy = (_sorts: PrintablePlayerSortName[]) => {
    updateSort(_sorts);
    setSearchParams(searchParams => {
      searchParams.set('s', _sorts.map(encodeURIComponent).join(','));
      return searchParams;
    });
  };

  const rowsNumber =
    (watchlistId && results?.nbHits) ||
    ((results?.nbHits || 0) < defaultRowsNumber && results?.nbHits) ||
    20;
  return (
    <SearchTable
      header={
        <SearchPlayerTableHeader
          sorts={sorts as PrintablePlayerSortName[]}
          refine={sortBy}
        />
      }
    >
      {loading ? (
        <SearchPlayerRowSkeletons rowsNumber={rowsNumber} />
      ) : (
        commonPlayers.map(({ anyPlayer, ...marketValues }) => {
          const scopedScores: Partial<
            Record<TotalAveragePlayerScore, number | null>
          > = {
            [AveragePlayerScore.SEASON_AVERAGE_SCORE]:
              anyPlayer.scopedSeasonAverage,
            [AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE]:
              anyPlayer.scopedLastFiveSo5Average,
            [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE]:
              anyPlayer.scopedLastTenPlayedSo5Average,
            [AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE]:
              anyPlayer.scopedLastFifteenSo5Average,
            [AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE]:
              anyPlayer.scopedLastFortySo5Average,
          };
          const recentGameTeamModes = isScopedTeamMode
            ? teamModesOfGameStats(anyPlayer.iconAnyGameStats)
            : undefined;
          const scopedBreakdowns: Partial<
            Record<AveragePlayerScore, number | null>
          > = {
            [AveragePlayerScore.LAST_TEN_PLAYED_AVERAGE_ALL_AROUND_SCORE]:
              anyPlayer.scopedLastTenAverageAllAroundScore,
            [AveragePlayerScore.LAST_TEN_PLAYED_AVERAGE_DECISIVE_SCORE]:
              anyPlayer.scopedLastTenAverageDecisiveScore,
            [AveragePlayerScore.LAST_FIFTEEN_AVERAGE_ALL_AROUND_SCORE]:
              anyPlayer.scopedLastFifteenAverageAllAroundScore,
            [AveragePlayerScore.LAST_FIFTEEN_AVERAGE_DECISIVE_SCORE]:
              anyPlayer.scopedLastFifteenAverageDecisiveScore,
          };
          const scopedFootballAverageStats =
            isScopedTeamMode && isType(anyPlayer, 'Player')
              ? (anyPlayer.scopedFootballAllAverageStats ?? undefined)
              : undefined;
          const scopedFootballTotalStats =
            isScopedTeamMode && isType(anyPlayer, 'Player')
              ? (anyPlayer.scopedFootballAllTotalStats ?? undefined)
              : undefined;
          return (
            <SearchPlayerRow
              key={anyPlayer.slug}
              anyPlayer={anyPlayer}
              onStatsClick={() => onStatsClick(anyPlayer.slug)}
              onListingsClick={rarity =>
                onListingsClick(anyPlayer.slug, rarity)
              }
              marketValues={marketValues}
              watchlistId={watchlistId}
              favoritesWatchlist={favoritesWatchlist}
              scopedScores={scopedScores}
              scopedBreakdowns={scopedBreakdowns}
              scopedFootballAverageStats={scopedFootballAverageStats}
              scopedFootballTotalStats={scopedFootballTotalStats}
              recentGameTeamModes={recentGameTeamModes}
              scopedAnyGameStats={
                isScopedTeamMode
                  ? (anyPlayer.scopedAnyGameStats ?? undefined)
                  : undefined
              }
              teamMode={isScopedTeamMode ? teamMode : null}
            />
          );
        })
      )}
    </SearchTable>
  );
};

BackendSearchPlayerTable.fragments = {
  AnyPlayerInterface: gql`
    fragment BackendSearchPlayerTable_AnyPlayerInterface on AnyPlayerInterface {
      slug
      ...SearchPlayerRow_anyPlayer
      scopedSeasonAverage: averageScore(
        type: SEASON_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastFiveSo5Average: averageScore(
        type: LAST_FIVE_SO5_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastTenPlayedSo5Average: averageScore(
        type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastFifteenSo5Average: averageScore(
        type: LAST_FIFTEEN_SO5_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastFortySo5Average: averageScore(
        type: LAST_FORTY_SO5_AVERAGE_SCORE
        teamMode: $teamMode
      )
      scopedLastTenAverageAllAroundScore: averageScore(
        type: LAST_TEN_PLAYED_AVERAGE_ALL_AROUND_SCORE
        teamMode: $teamMode
      )
      scopedLastTenAverageDecisiveScore: averageScore(
        type: LAST_TEN_PLAYED_AVERAGE_DECISIVE_SCORE
        teamMode: $teamMode
      )
      scopedLastFifteenAverageAllAroundScore: averageScore(
        type: LAST_FIFTEEN_AVERAGE_ALL_AROUND_SCORE
        teamMode: $teamMode
      )
      scopedLastFifteenAverageDecisiveScore: averageScore(
        type: LAST_FIFTEEN_AVERAGE_DECISIVE_SCORE
        teamMode: $teamMode
      )
      scopedAnyGameStats: anyGameStats(
        teamMode: $teamMode
        so5FixtureSlug: $so5FixtureSlug
        last: 10
      ) @skip(if: $withoutSo5Fixture) {
        id
        ...AnyPlayerUpcomingGames_anyGameStat
      }
      iconAnyGameStats: anyGameStats(last: 15) @skip(if: $skipTeamSlugs) {
        id
        anyTeam {
          slug
        }
      }
      ... on Player {
        slug
        scopedFootballAllAverageStats: allAverageStats(
          limit: $averageLimit
          teamMode: $teamMode
        ) {
          stat
          value
        }
        scopedFootballAllTotalStats: allTotalStats(teamMode: $teamMode) {
          stat
          value
        }
      }
    }
    ${SearchPlayerRow.fragments.anyPlayer}
    ${anyPlayerUpcomingGamesAnyGameStatFragment}
  ` as TypedDocumentNode<BackendSearchPlayerTable_AnyPlayerInterface>,
};
